import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "@tanstack/react-router";
import { ChevronLeft, AlertTriangle } from "lucide-react";
import { devicesApi } from "../api/queries/devices";
import { CapabilityBadges } from "../components/CapabilityBadges";
import { StatusDot, deviceTone } from "../components/StatusDot";
import { Modal } from "../components/Modal";
import { WorkspacesPanel } from "../components/WorkspacesPanel";
import { toast } from "../components/Toast";
import { formatRelative } from "../lib/time";
import { useUiStore } from "../stores/ui";
import { fleetKey } from "../hooks/useFleet";

const TABS = ["overview", "active", "history", "settings"] as const;
type Tab = (typeof TABS)[number];

export function DeviceDetailPage() {
  // Route id is auto-namespaced under the protected parent ("_protected"),
  // so the literal "/devices/$id" doesn't match the registered tree and
  // TanStack throws "Invariant failed". `strict: false` returns the union
  // of all params; the `id` is always present when this component renders.
  const { id } = useParams({ strict: false }) as { id: string };
  const nav = useNavigate();
  const qc = useQueryClient();
  const companyId = useUiStore((s) => s.activeCompanyId);

  const [tab, setTab] = useState<Tab>("overview");
  const [confirmRevoke, setConfirmRevoke] = useState(false);

  const device = useQuery({
    queryKey: ["device", id],
    queryFn: () => devicesApi.get(id),
  });

  const revoke = useMutation({
    mutationFn: () => devicesApi.revoke(id),
    onSuccess: () => {
      toast(`${device.data?.name ?? "Device"} revoked`, "success");
      if (companyId) qc.invalidateQueries({ queryKey: fleetKey(companyId) });
      nav({ to: "/" });
    },
    onError: (e: Error) => toast(e.message ?? "Failed to revoke", "error"),
  });

  if (device.isLoading)
    return (
      <main className="grid h-[calc(100vh-var(--topnav-h))] place-items-center">
        <span className="spinner" aria-hidden="true" />
      </main>
    );
  if (!device.data)
    return (
      <main className="grid h-[calc(100vh-var(--topnav-h))] place-items-center text-secondary">
        Device not found
      </main>
    );

  const d = device.data;
  const tone = deviceTone(d);

  return (
    <main className="mx-auto max-w-[1100px] px-6 py-6">
      <button
        type="button"
        onClick={() => nav({ to: "/" })}
        className="btn btn--ghost btn--sm mb-3"
      >
        <ChevronLeft size={14} /> Back to fleet
      </button>

      <header className="card flex flex-col gap-3 p-5">
        <div className="flex items-start gap-3">
          <StatusDot tone={tone} pulse={tone === "running"} size="lg" />
          <div className="flex-1 min-w-0">
            <h1 className="text-[20px] font-semibold tracking-[-0.01em]">{d.name}</h1>
            <div className="text-[13px] text-secondary font-mono">{d.hostname}</div>
            <div className="mt-1 text-[12px] text-tertiary">
              {d.online ? "Online" : `Last seen ${formatRelative(d.last_seen_at)}`} ·{" "}
              {d.pairing_state}
            </div>
          </div>
        </div>
        <CapabilityBadges caps={d.capabilities} expanded />
      </header>

      <nav role="tablist" aria-label="Device sections" className="mt-4 flex gap-1 border-b border-border-subtle">
        {TABS.map((t) => (
          <button
            key={t}
            role="tab"
            aria-selected={tab === t}
            className={`px-3 py-2 text-[13px] font-medium ${
              tab === t
                ? "text-primary border-b-2 border-primary"
                : "text-secondary hover:text-primary"
            }`}
            onClick={() => setTab(t)}
            type="button"
          >
            {t === "overview"
              ? "Overview"
              : t === "active"
                ? "Active step"
                : t === "history"
                  ? "History"
                  : "Settings"}
          </button>
        ))}
      </nav>

      <section className="mt-4">
        {tab === "overview" && (
          <div className="flex flex-col gap-4">
            <div className="grid gap-3 md:grid-cols-2">
              <Stat label="Adapters" value={d.capabilities.adapters.join(", ") || "—"} />
              <Stat label="GPU" value={d.capabilities.gpu.map((g) => `${g.model} (${g.vram_gb}GB)`).join(", ") || "—"} />
              <Stat label="CPU" value={`${d.capabilities.cpu.cores}c · ${d.capabilities.cpu.model}`} />
              <Stat label="Memory" value={`${d.capabilities.memory_gb}GB`} />
              <Stat label="Tags" value={d.capabilities.tags.join(", ") || "—"} />
              <Stat label="Paperclip" value={d.capabilities.paperclip_version} />
            </div>
            <WorkspacesPanel deviceId={id} />
          </div>
        )}
        {tab === "active" && (
          <div className="card px-6 py-8 text-center text-secondary">
            {d.current_step_id
              ? `Running step ${d.current_step_id}`
              : "No active step on this device."}
          </div>
        )}
        {tab === "history" && (
          <div className="card px-6 py-8 text-center text-secondary">
            History view coming soon.
          </div>
        )}
        {tab === "settings" && (
          <div className="card flex flex-col gap-3 p-5">
            <div>
              <h3 className="text-[13px] font-semibold">Danger zone</h3>
              <p className="text-[12px] text-secondary">
                Revoking the device will sign it out immediately. The row stays for audit.
              </p>
            </div>
            <button
              className="btn btn--danger self-start"
              onClick={() => setConfirmRevoke(true)}
              type="button"
            >
              Revoke device
            </button>
          </div>
        )}
      </section>

      <Modal
        open={confirmRevoke}
        onClose={() => setConfirmRevoke(false)}
        title="Revoke this device?"
        size="sm"
        footer={
          <>
            <button className="btn btn--ghost" onClick={() => setConfirmRevoke(false)} type="button">
              Cancel
            </button>
            <button
              className="btn btn--danger"
              onClick={() => revoke.mutate()}
              disabled={revoke.isPending}
              type="button"
            >
              Revoke
            </button>
          </>
        }
      >
        <div className="flex items-start gap-3">
          <AlertTriangle size={20} className="mt-1 text-status-error" aria-hidden="true" />
          <div className="text-[13px] text-secondary">
            <strong className="text-primary">{d.name}</strong> will be disconnected and any
            running steps cancelled. This action cannot be undone.
          </div>
        </div>
      </Modal>
    </main>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="card p-4">
      <div className="micro mb-1">{label}</div>
      <div className="text-[13px] text-primary font-mono break-all">{value}</div>
    </div>
  );
}
